import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TableSkeleton from "../common/TableSkeleton";
import AssignedAdvisersSection from "./AssignedAdvisersSection";
import { Client, Contact } from "./types";

interface ClientDetailPanelProps {
  client: Client | null;
  contacts: Contact[];
  isLoading: boolean;
  // True for the split second between mount and a persisted selection being resolved against
  // the freshly-fetched client list — shows a loading placeholder here instead of "Select a
  // Client", which would otherwise flash before the previous selection reappears.
  isRestoringSelection?: boolean;
  onOpenModal: (type: string, data?: any) => void;
}

const ClientDetailPanel = ({ client, contacts, isLoading, isRestoringSelection, onOpenModal }: ClientDetailPanelProps) => {
  if (!client) {
    if (isRestoringSelection) {
      return (
        <Paper sx={{ height: "100%", p: 3, borderRadius: "16px" }}>
          <Skeleton variant="text" width="35%" height={40} />
          <Skeleton variant="rounded" width={90} height={24} sx={{ mt: 1 }} />
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={3}>
            {[0, 1, 2].map((col) => (
              <Grid item xs={12} sm={4} key={col}>
                <Skeleton variant="text" width="60%" height={28} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="80%" />
                <Skeleton variant="text" width="70%" />
              </Grid>
            ))}
          </Grid>
        </Paper>
      );
    }

    return (
      <Paper
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          p: 3,
          borderRadius: "12px",
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 60, color: "text.secondary" }} />
        <Typography variant="h6" mt={2}>
          Select a Client
        </Typography>
        <Typography color="text.secondary">
          Choose a client from the list to view their details, linked contacts,
          and more.
        </Typography>
      </Paper>
    );
  }

  const primaryContact = contacts.find((c) => c.isPrimary);
  const { address, profile } = client as any;

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: "16px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {client.name}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" mt={1}>
              <Chip label={client.entityType} size="small" />
            </Stack>
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <Tooltip title="Edit Client">
              <IconButton
                size="small"
                onClick={() => onOpenModal("editClient", client)}
              >
                <EditIcon color="primary" fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Client">
              <IconButton
                size="small"
                onClick={() => onOpenModal("deleteClient", client)}
              >
                <DeleteIcon color="error" fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => onOpenModal("viewClient", client)}
              sx={{
                textTransform: "none",
                fontWeight: "bold",
              }}
            >
              View Full Profile
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Key Information
            </Typography>
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                Client Since:
                <Typography
                  component="span"
                  variant="body2"
                  color="text.primary"
                  ml={1}
                >
                  {new Date(client.createdAt).toLocaleDateString()}
                </Typography>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Primary Contact:
                <Typography
                  component="span"
                  variant="body2"
                  color="text.primary"
                  ml={1}
                >
                  {primaryContact ? primaryContact.name : "Not set"}
                </Typography>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Linked Contacts:
                <Typography
                  component="span"
                  variant="body2"
                  color="text.primary"
                  ml={1}
                >
                  {contacts.length}
                </Typography>
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Business Profile
            </Typography>
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                Status:
                <Typography
                  component="span"
                  variant="body2"
                  fontWeight={500}
                  ml={1}
                >
                  {profile?.clientStatus || "Not set"}
                </Typography>
              </Typography>

              {profile?.primaryIndustry ? (
                <Typography variant="body2" color="text.secondary">
                  Industry:
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.primary"
                    ml={1}
                  >
                    {profile.primaryIndustry}
                  </Typography>
                </Typography>
              ) : (
                <Typography variant="body2" color="text.disabled">
                  No industry listed
                </Typography>
              )}

              {profile?.companySize ? (
                <Typography variant="body2" color="text.secondary">
                  Company Size:
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.primary"
                    ml={1}
                  >
                    {profile.companySize}
                  </Typography>
                </Typography>
              ) : null}
            </Stack>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Address
            </Typography>
            {address && (address.line1 || address.city || address.country) ? (
              <Stack spacing={0}>
                {address.line1 && (
                  <Typography variant="body2">{address.line1}</Typography>
                )}
                {address.line2 && (
                  <Typography variant="body2">{address.line2}</Typography>
                )}
                {(address.city || address.state || address.zipCode) && (
                  <Typography variant="body2">
                    {[address.city, address.state, address.zipCode]
                      .filter(Boolean)
                      .join(", ")}
                  </Typography>
                )}
                {address.country && (
                  <Typography variant="body2">{address.country}</Typography>
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.disabled">
                No address listed
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          minHeight: 0,
          gap: 2,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            minWidth: 0,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
            flexShrink={0}
          >
            <Typography variant="h6" noWrap>
              Linked Contacts
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => onOpenModal("link", client)}
              sx={{ flexShrink: 0 }}
            >
              Link
            </Button>
          </Box>

          <Box sx={{ flexGrow: 1, overflowY: "auto", pr: 1, minHeight: 0 }}>
            {isLoading ? (
              <TableSkeleton rows={3} cols={2} rowHeight={44} />
            ) : contacts.length > 0 ? (
              <List dense sx={{ p: 0 }}>
                {contacts.map((contact) => (
                  <ListItem
                    key={contact._id}
                    secondaryAction={
                      <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                        <Tooltip title="Edit Contact">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => onOpenModal("editContact", contact)}
                          >
                            <EditIcon fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Unlink Contact">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() =>
                              onOpenModal("unlinkContact", contact)
                            }
                          >
                            <LinkOffIcon fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                    disablePadding
                    sx={{ pr: 10 }}
                  >
                    <ListItemText
                      disableTypography
                      primary={
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          overflow="hidden"
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ fontWeight: 500 }}
                          >
                            {contact.name}
                          </Typography>
                          <Chip
                            label={
                              contact.relationshipType || "No relationship"
                            }
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 20,
                              fontSize: "0.7rem",
                              flexShrink: 0,
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                          sx={{ display: "block", mt: 0.5 }}
                        >
                          {contact.email}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  No contacts linked.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Divider
          orientation="vertical"
          flexItem
          sx={{ display: { xs: "none", md: "block" } }}
        />
        <Divider sx={{ display: { xs: "block", md: "none" } }} />

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            minWidth: 0,
          }}
        >
          <AssignedAdvisersSection clientId={client._id} orgId={client.orgId} />
        </Box>
      </Box>
    </Paper>
  );
};

export default ClientDetailPanel;
